import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

interface Milestone {
  year: string;
  country: string;
  tagline: string;
  description: string;
  image: string;
}

const milestones: Milestone[] = [
  {
    year: '2016',
    country: 'Colombia',
    tagline: 'Sede Principal',
    description:
      'Nace Esencia&co. en Colombia, consolidándose como la mayor distribuidora de insumos de perfumería fina de Latinoamérica. Desde nuestra sede central, iniciamos operaciones con un compromiso inquebrantable de calidad y servicio.',
    image:
      'https://readdy.ai/api/search-image?query=breathtaking%20aerial%20view%20of%20lush%20green%20Colombian%20coffee%20plantations%20rolling%20hills%20bathed%20in%20warm%20golden%20sunrise%20light%20misty%20valleys%20elegant%20serene%20landscape%20vibrant%20cheerful%20atmosphere%20nature%20beauty&width=800&height=600&seq=news-colombia-v2&orientation=landscape',
  },
  {
    year: '2017',
    country: 'México',
    tagline: 'Nueva Frontera',
    description:
      'Damos el primer paso de nuestra expansión internacional abriendo operaciones en México. El mercado mexicano recibe con entusiasmo nuestra propuesta de insumos de perfumería fina, marcando el inicio de un crecimiento sostenido.',
    image:
      'https://readdy.ai/api/search-image?query=vibrant%20elegant%20Mexican%20agave%20field%20at%20golden%20hour%20sunset%20warm%20amber%20light%20towering%20blue%20agave%20plants%20cheerful%20festive%20atmosphere%20beautiful%20landscape%20photography%20serene%20and%20luxurious%20mood&width=800&height=600&seq=news-mexico-v2&orientation=landscape',
  },
  {
    year: '2018',
    country: 'Panamá',
    tagline: 'Puente del Mundo',
    description:
      'Panamá se convierte en nuestro siguiente destino, posicionándose como un hub estratégico para la distribución de insumos en Centroamérica. La apertura fortalece nuestra red logística regional.',
    image:
      'https://readdy.ai/api/search-image?query=stunning%20Panama%20City%20skyline%20at%20sunset%20modern%20skyscrapers%20reflecting%20golden%20orange%20light%20on%20the%20ocean%20elegant%20cosmopolitan%20atmosphere%20cheerful%20warm%20tropical%20evening%20luxury%20cityscape%20photography&width=800&height=600&seq=news-panama-v2&orientation=landscape',
  },
  {
    year: '2019',
    country: 'Venezuela',
    tagline: 'Presencia Bolivariana',
    description:
      'Extendemos nuestra red de distribución a Venezuela, llevando insumos de perfumería fina a un mercado con gran tradición en belleza y cuidado personal. Nuestra presencia consolida el liderazgo regional.',
    image:
      'https://readdy.ai/api/search-image?query=majestic%20Angel%20Falls%20in%20Venezuela%20worlds%20highest%20waterfall%20surrounded%20by%20lush%20tropical%20rainforest%20golden%20sunlight%20breaking%20through%20mist%20cheerful%20serene%20natural%20wonder%20elegant%20landscape%20photography%20vibrant%20greens%20and%20warm%20light&width=800&height=600&seq=news-venezuela-v2&orientation=landscape',
  },
  {
    year: '2020',
    country: 'Ecuador',
    tagline: 'Raíces Andinas',
    description:
      'Ecuador se suma a nuestra red de distribución, ampliando el alcance de insumos de perfumería fina en la región andina. La apertura refuerza nuestro compromiso de abastecer a cada país latinoamericano.',
    image:
      'https://readdy.ai/api/search-image?query=spectacular%20snow-capped%20Cotopaxi%20volcano%20in%20Ecuador%20rising%20above%20golden%20rolling%20Andean%20plains%20at%20sunrise%20cheerful%20warm%20light%20on%20green%20meadows%20elegant%20majestic%20landscape%20photography%20serene%20atmosphere&width=800&height=600&seq=news-ecuador-v2&orientation=landscape',
  },
  {
    year: '2021',
    country: 'Bolivia',
    tagline: 'Altura Superior',
    description:
      'Llegamos a Bolivia, llevando nuestra experiencia en distribución de insumos de perfumería fina a nuevos mercados. Cada apertura refuerza nuestra posición como líderes indiscutibles en Latinoamérica.',
    image:
      'https://readdy.ai/api/search-image?query=otherworldly%20Salar%20de%20Uyuni%20salt%20flat%20in%20Bolivia%20at%20golden%20hour%20mirror%20reflections%20of%20dramatic%20clouds%20on%20pristine%20white%20salt%20surface%20cheerful%20surreal%20landscape%20photography%20elegant%20warm%20sunset%20light&width=800&height=600&seq=news-bolivia-v2&orientation=landscape',
  },
  {
    year: '2022',
    country: 'República Dominicana',
    tagline: 'Cálidez Caribeña',
    description:
      'Cerramos nuestro ciclo de expansión con la apertura en República Dominicana, consolidando una presencia que abarca desde México hasta el Caribe. Hoy somos la red de distribución de insumos de perfumería fina más grande de la región.',
    image:
      'https://readdy.ai/api/search-image?query=pristine%20Caribbean%20beach%20in%20Dominican%20Republic%20turquoise%20crystal%20water%20white%20sand%20palm%20trees%20swaying%20in%20warm%20breeze%20golden%20sunset%20light%20cheerful%20tropical%20paradise%20elegant%20serene%20luxury%20vacation%20atmosphere&width=800&height=600&seq=news-rd-v2&orientation=landscape',
  },
];

function MilestoneCard({ milestone, index }: { milestone: Milestone; index: number }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const isEven = index % 2 === 0;

  return (
    <div ref={ref} className="relative flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
      {/* Timeline dot */}
      <div className="hidden lg:flex absolute left-1/2 top-8 -translate-x-1/2 flex-col items-center z-10">
        <div className="w-4 h-4 rounded-full bg-gray-900 border-4 border-white shadow-md" />
        <div className="w-px h-full bg-gray-200" />
      </div>

      {/* Image */}
      <div
        className={`w-full lg:w-1/2 ${isEven ? 'lg:order-1' : 'lg:order-2'} transition-all duration-1000 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="relative overflow-hidden rounded-lg aspect-[4/3]">
          <img
            src={milestone.image}
            alt={milestone.country}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          <div className="absolute bottom-4 left-4">
            <span className="inline-block px-3 py-1 bg-white/90 text-gray-900 text-xs font-semibold uppercase tracking-wider rounded-full">
              {milestone.year}
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div
        className={`w-full lg:w-1/2 ${isEven ? 'lg:order-2 lg:pl-12' : 'lg:order-1 lg:pr-12 lg:text-right'} transition-all duration-1000 delay-200 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-2">{milestone.tagline}</p>
        <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{milestone.country}</h3>
        <p className="text-gray-600 leading-relaxed text-sm md:text-base">{milestone.description}</p>
      </div>
    </div>
  );
}

export default function ExpansionTimeline() {
  const [visible, setVisible] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const [ctaVisible, setCtaVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2 }
    );
    if (headerRef.current) observer.observe(headerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCtaVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2 }
    );
    if (ctaRef.current) observer.observe(ctaRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-20 md:py-28 px-6 md:px-10 lg:px-16 bg-white">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div ref={headerRef} className={`text-center mb-20 transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-xs uppercase tracking-[0.25em] text-gray-500 mb-4">Nuestra Expansión</p>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
            De Colombia al Caribe
          </h2>
          <div className="w-16 h-px bg-gray-300 mx-auto mb-6" />
          <p className="text-gray-600 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
            Desde 2016, hemos crecido paso a paso hasta convertirnos en la red de distribución de insumos de perfumería fina más importante de Latinoamérica. Cada país representa un hito en nuestra historia.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Center line - desktop only */}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-gray-200 -translate-x-1/2" />

          <div className="flex flex-col gap-16 lg:gap-24">
            {milestones.map((milestone, index) => (
              <MilestoneCard key={milestone.country} milestone={milestone} index={index} />
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div
          ref={ctaRef}
          className={`mt-24 md:mt-32 text-center transition-all duration-1000 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-[0.25em] text-gray-500 mb-4">Sigamos Creciendo</p>
            <h3 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
              ¿Quieres llevar Esencia&co. a tu país?
            </h3>
            <p className="text-gray-600 leading-relaxed text-sm md:text-base mb-8">
              Estamos abiertos a nuevas oportunidades de expansión. Si representas un mercado potencial en Latinoamérica, contáctanos y exploremos juntos la posibilidad de abrir operaciones en tu región.
            </p>
            <Link
              to="/contact"
              className="inline-block px-10 py-4 bg-gray-900 text-white text-sm tracking-widest uppercase hover:bg-gray-800 transition-colors rounded-md whitespace-nowrap"
            >
              Contactar Ahora
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}